/**
 * useIntegrationRequestLogs — Integration Hub
 * -----------------------------------------------------------------------------
 * Logs técnicos das chamadas feitas aos aplicativos integrados (consultas,
 * testes de conexão, verificações de saúde e erros).
 *
 * Regras: acesso apenas por RPC SECURITY DEFINER, restrita a administradores
 * do tenant. Nenhum dado sensível (token, documento, PDF) é exposto.
 *
 * @version 1.0.0
 * RELEASE: 2026-08-29 v1.0.0 - Criação inicial (aba Logs do conector).
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

const logger = createLogger('IntegrationRequestLogs');

/** Linha do log técnico */
export interface IntegrationLogItem {
  id: string;
  created_at: string;
  provider_slug: string;
  connection_name: string | null;
  action: string;
  operation_code: string | null;
  status: string;
  http_status: number | null;
  duration_ms: number | null;
  request_id: string | null;
  error_code: string | null;
  error_message: string | null;
  user_name: string | null;
  total_count: number;
}

/** Filtros da listagem de logs */
export interface IntegrationLogFilters {
  providerSlug?: string | null;
  connectionId?: string | null;
  operationCode?: string | null;
  status?: string | null;
  dateFrom?: string | null;
  dateTo?: string | null;
  page?: number;
  pageSize?: number;
}

/** Lista paginada dos logs técnicos */
export function useIntegrationRequestLogs(filters: IntegrationLogFilters, enabled = true) {
  const pageSize = filters.pageSize ?? 20;
  const page = filters.page ?? 1;

  const query = useQuery({
    queryKey: ['integration-request-logs', filters],
    enabled,
    staleTime: 15 * 1000,
    queryFn: async (): Promise<IntegrationLogItem[]> => {
      const { data, error } = await supabase.rpc('list_integration_request_logs', {
        p_provider_slug: filters.providerSlug ?? null,
        p_connection_id: filters.connectionId ?? null,
        p_operation_code: filters.operationCode ?? null,
        p_status: filters.status ?? null,
        p_date_from: filters.dateFrom ?? null,
        p_date_to: filters.dateTo ?? null,
        p_limit: pageSize,
        p_offset: (page - 1) * pageSize,
        p_tenant_hint: null,
      });

      if (error) {
        logger.error('list_integration_request_logs', { code: error.code });
        throw new Error('Não foi possível carregar os logs da integração');
      }
      return (data ?? []) as unknown as IntegrationLogItem[];
    },
  });

  const items = query.data ?? [];

  return {
    items,
    total: Number(items[0]?.total_count ?? 0),
    page,
    pageSize,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    refetch: query.refetch,
  };
}
